import api from './api';

export interface TopicData {
  name: string;
  count: number;
  [key: string]: string | number;
}

export interface Team {
  team_id: number;
  team_name: string;
}

export interface DashboardStats {
  call_stats: {
    total: number;
    inbound: number;
    outbound: number;
    avg_duration: number;
    unique_agents: number;
    today_calls: number;
    yesterday_calls: number;
    this_week_calls: number;
    this_month_calls: number;
    pct_change: number;
    last_update: string | null;
  };
  sentiments: {
    positive: number;
    neutral: number;
    negative: number;
  };
  summaries: {
    total: number;
    today: number;
    this_week: number;
  };
}

export interface CallSummary {
  id: number;
  call_id: number;
  date: string;
  duration: number;
  direction: string;
  agent_name: string;
  caller: string;
  summary: string;
  topics: string[];
  sentiment: string;
}

class AircallService {
  /**
   * Get list of teams
   */
  async getTeams(): Promise<Team[]> {
    const response = await api.get<Team[]>('/aircall/teams');
    return response.data;
  }

  /**
   * Get dashboard statistics
   */
  async getDashboardStats(teamIds?: number[]): Promise<DashboardStats> {
    let url = '/aircall/dashboard-stats';
    if (teamIds && teamIds.length > 0) {
      url += `?team_ids=${teamIds.join(',')}`;
    }
    const response = await api.get<DashboardStats>(url);
    return response.data;
  }

  /**
   * Get top topics from Aircall calls
   */
  async getTopTopics(limit: number = 20, teamIds?: number[]): Promise<TopicData[]> {
    let url = `/aircall/top-topics?limit=${limit}`;
    if (teamIds && teamIds.length > 0) {
      url += `&team_ids=${teamIds.join(',')}`;
    }
    const response = await api.get<TopicData[]>(url);
    return response.data;
  }

  /**
   * Get total count of unique topics
   */
  async getTopicsCount(teamIds?: number[]): Promise<number> {
    let url = '/aircall/topics-count';
    if (teamIds && teamIds.length > 0) {
      url += `?team_ids=${teamIds.join(',')}`;
    }
    const response = await api.get<{ total_count: number }>(url);
    return response.data.total_count;
  }

  /**
   * Get call summaries by sentiment and/or topic
   */
  async getCallSummaries(sentiment?: string, topic?: string, limit: number = 1000, teamIds?: number[]): Promise<CallSummary[]> {
    let url = `/aircall/call-summaries?limit=${limit}`;
    if (sentiment) url += `&sentiment=${sentiment}`;
    if (topic) url += `&topic=${encodeURIComponent(topic)}`;
    if (teamIds && teamIds.length > 0) url += `&team_ids=${teamIds.join(',')}`;
    const response = await api.get<CallSummary[]>(url);
    return response.data;
  }

  /**
   * Get sentiment breakdown for a specific topic
   */
  async getTopicSentiments(topic: string, teamIds?: number[]): Promise<{ positive: number; neutral: number; negative: number }> {
    let url = `/aircall/topic-sentiments?topic=${encodeURIComponent(topic)}`;
    if (teamIds && teamIds.length > 0) url += `&team_ids=${teamIds.join(',')}`;
    const response = await api.get<{ positive: number; neutral: number; negative: number }>(url);
    return response.data;
  }
}

const aircallService = new AircallService();
export default aircallService;
